import { cx } from 'emotion';
import {
  Fragment, ReactNode, useContext, useMemo,
} from 'react';
import { useSelector } from 'react-redux';

import { useOrientationChange } from '@ezugi/hooks';
import { DeviceInfo } from '@ezugi/utils';

import ChipSelector from '../../components/ChipSelector';
import { canPlaceBetsSelector, isDisconnectedOrReconnectingSelector } from '../../store/selectors/round';
import { BetToolbarContext } from './BetToolbarContext';
import ClearButton from './components/ClearButton';
import DoubleButton from './components/DoubleButton';
import RebetButton from './components/RebetButton';
import UndoButton from './components/UndoButton';
import { ButtonGroup, verticalButtonGroup } from './components/style';
import * as styles from './style';

const { Container, BetTools: BetToolsContainer } = styles;

const BetToolbarChipSelector = () => {
  const { vertical } = useContext(BetToolbarContext);
  const orientation = useOrientationChange();
  const isLandscape = orientation === 'landscape';
  const chipSelectorOrientation = vertical ?? (DeviceInfo.isHandheld && isLandscape);

  return (
    <ChipSelector
      className={cx(styles.chipSelector, 'bet-toolbar__chip-selector')}
      vertical={chipSelectorOrientation}
    />
  );
};

const DoubleRebetButton = ({ className, hasLabel }: {className?: string, hasLabel?: boolean}) => (
  <Fragment>
    <DoubleButton className={className} hasLabel={hasLabel} />
    <RebetButton className={className} hasLabel={hasLabel} />
  </Fragment>
);

const BetTools = ({ children, className }: {children: ReactNode, className?: string}) => {
  const { vertical } = useContext(BetToolbarContext);

  return (
    <BetToolsContainer
      className={cx({
        [styles.mobileBetTools]: DeviceInfo.isHandheld,
        [styles.verticalBetTools]: vertical,
      }, className)}
    >
      {children}
    </BetToolsContainer>
  );
};

export type BetToolbarProps = {
  children: ReactNode;
  vertical?: boolean;
  className?: string;
  externalShowHideBehaviour?: boolean;
};

const Left = ({ children }: {children: ReactNode}) => {
  const { vertical } = useContext(BetToolbarContext);
  const isHandheldVertical = DeviceInfo.isHandheld && vertical;

  return (
    <ButtonGroup
      className={cx(
        { [verticalButtonGroup]: isHandheldVertical },
        'bet-toolbar__button-group--left',
      )}
      data-e2e="bet-toolbar__button-group--left"
    >
      {children}
    </ButtonGroup>
  );
};

const Right = ({ children }: {children: ReactNode}) => {
  const { vertical } = useContext(BetToolbarContext);
  const isHandheldVertical = DeviceInfo.isHandheld && vertical;

  return (
    <ButtonGroup
      data-e2e="bet-toolbar__button-group--right"
      className={cx(
        { [verticalButtonGroup]: isHandheldVertical },
        'bet-toolbar__button-group--right'
      )}
    >
      {children}
    </ButtonGroup>
  );
};

export const BetToolbar = ({
  className,
  vertical = false,
  externalShowHideBehaviour = false,
  children,
  ...props
}: BetToolbarProps) => {
  const canPlaceBets = useSelector(canPlaceBetsSelector);
  const disconnected = useSelector(isDisconnectedOrReconnectingSelector);
  const contextValue = useMemo(() => ({ vertical }), [ vertical ]);

  if (disconnected || (!canPlaceBets && !externalShowHideBehaviour)) {
    return null;
  }

  return (
    <BetToolbarContext.Provider value={contextValue}>
      <Container
        className={cx(
          'bet-toolbar',
          {
            [styles.mobile]: DeviceInfo.isHandheld,
            [styles.vertical]: vertical,
            [styles.hiddenClassName]: externalShowHideBehaviour,
          },
          className
        )}
        {...props}
        data-e2e="bet-toolbar"
        data-testid="bet-toolbar"
      >
        {children}
      </Container>
    </BetToolbarContext.Provider>
  );
};

BetToolbar.Left = Left;
BetToolbar.Right = Right;
BetToolbar.Undo = UndoButton;
BetToolbar.ChipSelector = BetToolbarChipSelector;
BetToolbar.Double = DoubleButton;
BetToolbar.Clear = ClearButton;
BetToolbar.Rebet = RebetButton;
BetToolbar.DoubleRebet = DoubleRebetButton;
BetToolbar.BetTools = BetTools;

type BetToolbarOverrides = {
  disableRebetOverride?: boolean;
  disableUndoOverride?: boolean;
  disableDoubleOverride?: boolean;
};

export type EzugiBetToolbarProps = Omit<BetToolbarProps, 'children'> & BetToolbarOverrides

export const EzugiBetToolbar = (
  { disableDoubleOverride,
    disableRebetOverride,
    disableUndoOverride,
    ...props }: EzugiBetToolbarProps
) => (
  <BetToolbar {...props}>
    <BetToolbar.BetTools>
      <BetToolbar.Left>
        <BetToolbar.Undo disabled={disableUndoOverride} />
      </BetToolbar.Left>
      <BetToolbar.ChipSelector />
      <BetToolbar.Right>
        <BetToolbar.Double disabled={disableDoubleOverride} />
        <BetToolbar.Rebet disabled={disableRebetOverride} />
      </BetToolbar.Right>
    </BetToolbar.BetTools>
  </BetToolbar>
);
